import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext, FormikHelpers } from "formik";
import * as Yup from "yup";
import Modal from "../../../components/Modal";
import { Dropdown } from "../../../components/Dropdown";
import Switch from "../../../components/Switch";
import { Button } from "../../../components/Button";
import { apiGet, apiPost, apiPut } from "../../../services/apiServices";
import { endPoints } from "../../../configs/apiUrls";
import { toast } from "react-toastify";
import indiaData from "../../../data/stateData.json";
import { StreetFormValues } from "./types";

/* =====================================================
   Props
===================================================== */
interface AddStreetProps {
  mode: "add" | "edit";
  initialData?: any;
  isOpen: boolean;
  setShowAddModal: (v: boolean) => void;
  onSubmit: () => void;
  onCancel?: () => void;
}

/* =====================================================
   Inner Form Component
===================================================== */
const StreetForm = ({
  mode,
  setShowAddModal,
  showLoader,
  handleCancel,
}: {
  mode: "add" | "edit";
  setShowAddModal: (v: boolean) => void;
  showLoader: boolean;
  handleCancel?: () => void;
}) => {
  const { values, setFieldValue } = useFormikContext<StreetFormValues>();

  const [cityOptions, setCityOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [areaOptions, setAreaOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const isFirstStateLoad = useRef(true);
  const isFirstCityLoad = useRef(true);

  /* ===============================
     STATE → CITY
  =============================== */
  useEffect(() => {
    if (!values.state) {
      setCityOptions([]);
      setAreaOptions([]);
      return;
    }

    const stateObj = indiaData.find((s) => s.state === values.state);

    const cities =
      stateObj?.districts.map((d) => ({
        label: d,
        value: d,
      })) || [];

    setCityOptions(cities);

    // ❗ Reset ONLY when user changes state
    if (!isFirstStateLoad.current) {
      setFieldValue("city", "");
      setFieldValue("area", "");
    }

    isFirstStateLoad.current = false;
  }, [values.state, setFieldValue]);


  /* ===============================
     CITY → AREA (API)
  =============================== */
  useEffect(() => {
    const fetchAreas = async () => {
      if (!values.city || !values.state) {
        setAreaOptions([]);
        return;
      }

      try {
        const res = await apiGet(endPoints.GET_AREA_LIST, {
          state: values.state,
          city: values.city,
          isActive: true,
          limit:100000

        });

        setAreaOptions(
          res.data.map((a: any) => ({
            label: a.area,
            value: a._id,
          }))
        );

        // ❗ Reset ONLY when user changes city
        if (!isFirstCityLoad.current) {
          setFieldValue("area", "");
        }

        isFirstCityLoad.current = false;
      } catch {
        setAreaOptions([]);
      }
    };

    fetchAreas();
  }, [values.city, values.state, setFieldValue]);


  /* ===============================
     UI
  =============================== */

  return (
    <Form className="space-y-5 p-4">
      <h2 className="text-xl font-semibold">
        {mode === "add" ? "Add Street" : "Edit Street"}
      </h2>

      {/* STATE */}
      <div className="flex flex-col">
        <label className="text-sm font-semibold text-gray-700 mb-1.5">State *</label>
        <Dropdown
          options={indiaData.map((s) => ({
            label: s.state,
            value: s.state,
          }))}
          value={values.state}
          onChange={(val) => setFieldValue("state", val)}
          placeholder="Select State"
          className="w-full"
        />
        <ErrorMessage name="state" component="div" className="text-errorText text-xs mt-1" />
      </div>

      {/* CITY */}
      <div className="flex flex-col">
        <label className="text-sm font-semibold text-gray-700 mb-1.5">City *</label>
        <Dropdown
          options={cityOptions}
          value={values.city}
          onChange={(val) => setFieldValue("city", val)}
          placeholder="Select City"
          disabled={!values.state}
          className="w-full"
        />
        <ErrorMessage name="city" component="div" className="text-errorText text-xs mt-1" />
      </div>

      {/* AREA */}
      <div className="flex flex-col">
        <label className="text-sm font-semibold text-gray-700 mb-1.5">Area *</label>
        <Dropdown
          options={areaOptions}
          value={values.area}
          onChange={(opt: any) => setFieldValue("area", opt || "")}
          placeholder="Select Area"
          className="w-full"
          disabled={!values.city}
        />
        <ErrorMessage name="area" component="div" className="text-errorText text-xs mt-1" />
      </div>

      {/* STREET */}
      <div className="flex flex-col">
        <label className="text-sm font-semibold text-gray-700 mb-1.5">Street Name *</label>
        <Field
          name="street"
          placeholder="Enter street name"
          className="w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <ErrorMessage name="street" component="div" className="text-errorText text-xs mt-1" />
      </div>

      {mode === "edit" && (
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mt-2">
          <Switch
            checked={values.isActive}
            onCheckedChange={(v) => setFieldValue("isActive", v)}
            label="Active"
          />
        </div>
      )}

      {/* ACTIONS */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4">
        <Button
          type="button"
          variant="error"
          onClick={() => {
            setShowAddModal(false);
            handleCancel?.();
          }}
        >
          Cancel
        </Button>

        <Button disabled={showLoader} type="submit">
          {showLoader ? "Saving..." : mode === "add" ? "Add Street" : "Update"}
        </Button>
      </div>
    </Form>
  );
};

/* =====================================================
   Main Component
===================================================== */
const AddStreet = ({
  mode,
  initialData,
  isOpen,
  setShowAddModal,
  onSubmit,
  onCancel,
}: AddStreetProps) => {
  const [showLoader, setShowLoader] = useState(false);

  const initialValues: StreetFormValues = {
    state: initialData?.area?.state || "",
    city: initialData?.area?.city || "",
    area: initialData?.area?._id || "",
    street: initialData?.street || "",
    isActive: initialData?.isActive ?? true,
  };

  const validationSchema = Yup.object({
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area is required"),
    street: Yup.string().trim().required("Street is required"),
  });


  // 🚨 Add FormikHelpers to manipulate the form after submission
  const handleSubmit = async (
    values: StreetFormValues,
    { setFieldValue }: FormikHelpers<StreetFormValues>
  ) => {
    setShowLoader(true);
    try {
      const res =
        mode === "edit"
          ? await apiPut(`${endPoints.GET_STREET_LIST}/${initialData._id}`, values)
          : await apiPost(endPoints.GET_STREET_LIST, values);

      if (res.success) {
        toast.success(res.message || "Street saved successfully");

        // Notify parent table to refresh
        onSubmit();

        if (mode === "edit") {
          // Close modal when done editing
          setShowAddModal(false);
        } else {
          // 🚨 In add mode, keep State, City, Area, but clear Street
          setFieldValue("street", "");
        }
      } else {
        toast.error("Failed to save street");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to save street");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Modal onBackdropClick = {() =>{
      
    }}  isOpen={isOpen} onClose={() => setShowAddModal(false)}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <StreetForm
          mode={mode}
          setShowAddModal={setShowAddModal}
          handleCancel={onCancel}
          showLoader={showLoader}
        />
      </Formik>
    </Modal>
  );
};

export default AddStreet;